import { ICustomerVehicleOrdersHook } from '@models';
import { OrderService } from '@services';
import { useStore } from '@stores';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface IProps {
  customerId: string | undefined;
  registrationNumber: string | undefined;
}

export const useCustomerVehicleOrders = ({
  customerId,
  registrationNumber,
}: IProps): ICustomerVehicleOrdersHook => {
  const { orderStore } = useStore();
  const { orders, totalCount, currentPage, skipCount, maxResultCount, loading, handlePageChange } =
    orderStore;
  const [service, setService] = useState<string>();
  const [status, setStatus] = useState<string[]>();
  const [sorting, setSorting] = useState<string | undefined>();

  const statusParam = useMemo(() => (status?.length ? status.join(',') : undefined), [status]);

  useEffect(() => {
    // registrationNumber is optional: when omitted ("All vehicles") we fetch every order for the customer.
    customerId &&
      orderStore.getAll({
        customerId,
        registrationNumber,
        status: statusParam,
        service,
        maxResultCount,
        skipCount,
        sorting,
      });
  }, [orderStore, service, customerId, registrationNumber, statusParam, skipCount, maxResultCount, sorting]);

  useEffect(() => {
    return () => orderStore.resetOrders();
  }, [orderStore]);

  const handleSortChange = (value?: string) => {
    setSorting(value);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleServiceSearch = debounce((text: string) => {
    setService(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  }, 500);

  const handleStatusChange = (values?: string[]) => {
    setStatus(values?.length ? values : undefined);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await OrderService.getAll({
      customerId,
      registrationNumber,
      status: statusParam,
      service,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
      sorting,
    });
    return result.items;
  }, [customerId, registrationNumber, statusParam, service, totalCount, sorting]);

  return {
    loading,
    status,
    dataSource: Array.from(orders.values()),
    totalCount,
    skipCount,
    currentPage,
    maxResultCount,
    handleServiceSearch,
    handleStatusChange,
    handlePageChange,
    handleSortChange,
    sorting,
    fetchAllForExport,
  };
};
